import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  Alert
} from "@mui/material";
import { getDbStats, getDbSummary, getFileStats, getFileSummary } from "../api/ragDocsApi";

import "./rag-docs-dashboard.css";

export default function RagDocsDashboard() {
  const [activeTab, setActiveTab] = useState("db");

  // Filter States
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("ALL");
  const [availableDocTypes, setAvailableDocTypes] = useState(["ALL"]);

  // Pagination States
  const [dbPage, setDbPage] = useState(0);
  const [dbRowsPerPage, setDbRowsPerPage] = useState(5);

  const [filePage, setFilePage] = useState(0);
  const [fileRowsPerPage, setFileRowsPerPage] = useState(5);

  const [dbSummary, setDbSummary] = useState([]);
  const [dbRows, setDbRows] = useState([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [dbError, setDbError] = useState(false);

  const [fileSummary, setFileSummary] = useState({});
  const [fileRows, setFileRows] = useState([]);
  const [fileLoading, setFileLoading] = useState(true);
  const [fileError, setFileError] = useState(false);

  // Sorting state
  const [dbSortOrder, setDbSortOrder] = useState("desc");
  const [fileSortOrder, setFileSortOrder] = useState("desc");

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = (params) => {
    loadDb(params);
    loadFile(params);
  };

  const loadDb = async (customParams) => {
    setDbLoading(true);
    setDbError(false);

    const params = customParams || {
      from: fromDate || undefined,
      to: toDate || undefined,
      page: dbPage,
      size: dbRowsPerPage,
      documentType: docTypeFilter || undefined
    };

    try {
      const [summaryRes, rowsRes] = await Promise.all([
        getDbSummary(params),
        getDbStats(params)
      ]);
      if (summaryRes.status && rowsRes.status) {
        setDbSummary(summaryRes.data || {});
        setDbRows(rowsRes.data || []);

        // Dynamic doc types parsing from response when loading all doc types
        if (!params.documentType || params.documentType === "ALL") {
          const types = ["ALL", ...new Set((rowsRes.data || []).map((r) => r.aiDocsProcessKey?.documentType).filter(Boolean))];
          setAvailableDocTypes(types);
        }
      } else {
        setDbError(true);
      }
    } catch (error) {
      setDbError(true);
    } finally {
      setDbLoading(false);
    }
  };

  const loadFile = async (customParams) => {
    setFileLoading(true);
    setFileError(false);

    const params = customParams || {
      from: fromDate || undefined,
      to: toDate || undefined,
      page: filePage,
      size: fileRowsPerPage
    };

    try {
      const [summaryRes, rowsRes] = await Promise.all([
        getFileSummary(params),
        getFileStats(params)
      ]);
      if (summaryRes.status && rowsRes.status) {
        setFileSummary(summaryRes.data || {});
        setFileRows(rowsRes.data || []);
      } else {
        setFileError(true);
      }
    } catch (error) {
      setFileError(true);
    } finally {
      setFileLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setDbPage(0);
    setFilePage(0);

    loadAll({
      from: fromDate || undefined,
      to: toDate || undefined,
      page: 0,
      size: activeTab === "db" ? dbRowsPerPage : fileRowsPerPage,
      documentType: docTypeFilter || undefined
    });
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setDocTypeFilter("ALL");
    setDbPage(0);
    setFilePage(0);

    loadAll({
      from: undefined,
      to: undefined,
      page: 0,
      size: activeTab === "db" ? dbRowsPerPage : fileRowsPerPage,
      documentType: undefined
    });
  };

  const handleDbPageChange = (event, newPage) => {
    setDbPage(newPage);
    loadDb({
      from: fromDate || undefined,
      to: toDate || undefined,
      page: newPage,
      size: dbRowsPerPage,
      documentType: docTypeFilter || undefined
    });
  };

  const handleDbRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setDbRowsPerPage(newSize);
    setDbPage(0);
    loadDb({
      from: fromDate || undefined,
      to: toDate || undefined,
      page: 0,
      size: newSize,
      documentType: docTypeFilter || undefined
    });
  };

  const handleFilePageChange = (event, newPage) => {
    setFilePage(newPage);
    loadFile({
      from: fromDate || undefined,
      to: toDate || undefined,
      page: newPage,
      size: fileRowsPerPage
    });
  };

  const handleFileRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setFileRowsPerPage(newSize);
    setFilePage(0);
    loadFile({
      from: fromDate || undefined,
      to: toDate || undefined,
      page: 0,
      size: newSize
    });
  };

  const handleDocTypeChange = (e) => {
    const val = e.target.value;
    setDocTypeFilter(val);
    setDbPage(0);

    loadDb({
      from: fromDate || undefined,
      to: toDate || undefined,
      page: 0,
      size: dbRowsPerPage,
      documentType: val || undefined
    });
  };

  const dbSummaryItem = Array.isArray(dbSummary)
    ? dbSummary[0] || {}
    : dbSummary || {};

  const fileSummaryItem = Array.isArray(fileSummary)
    ? fileSummary[0] || {}
    : fileSummary || {};

  // Backend does the filtering, so we map dbRows directly
  const filteredDbRows = dbRows;

  // Total counts derived safely from active summaries
  const dbTotalCount = Number(dbSummaryItem.totalDocs) || 0;
  const fileTotalCount = Number(fileSummaryItem.totalFiles) || 0;

  // ── Cost aggregates computed from rows ──────────────────────────────────────

  const dbTotalCostUsd = filteredDbRows.reduce((sum, r) => sum + (r.ocrCostUsd || 0), 0);
  const dbTotalCostInr = filteredDbRows.reduce((sum, r) => sum + (r.ocrCostInr || 0), 0);
  const dbTotalInputTokens = filteredDbRows.reduce((sum, r) => sum + (r.ocrInputTokens || 0), 0);
  const dbTotalOutputTokens = filteredDbRows.reduce((sum, r) => sum + (r.ocrOutputTokens || 0), 0);

  const processed = filteredDbRows.filter((r) => r.ocrCostInr > 0);
  const dbAvgCostInr = processed.length ? dbTotalCostInr / processed.length : 0;

  const rateRow = filteredDbRows.find((r) => r.usdToInrRate > 0);
  const dbUsdToInrRate = rateRow ? rateRow.usdToInrRate : 0;

  // ── Sorted Rows ─────────────────────────────────────────────────────────────

  const sortedDbRows = [...filteredDbRows].sort((a, b) => {
    const dateA = a.aiDocsProcessKey?.broadcastingDateTime;
    const dateB = b.aiDocsProcessKey?.broadcastingDateTime;
    if (!dateA) return dbSortOrder === "desc" ? 1 : -1;
    if (!dateB) return dbSortOrder === "desc" ? -1 : 1;
    const timeA = new Date(dateA).getTime();
    const timeB = new Date(dateB).getTime();
    if (!isNaN(timeA) && !isNaN(timeB)) {
      return dbSortOrder === "desc" ? timeB - timeA : timeA - timeB;
    }
    return dbSortOrder === "desc" ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
  });

  const sortedFileRows = [...fileRows].sort((a, b) => {
    const dateA = a.timestamp;
    const dateB = b.timestamp;
    if (!dateA) return fileSortOrder === "desc" ? 1 : -1;
    if (!dateB) return fileSortOrder === "desc" ? -1 : 1;
    const timeA = new Date(dateA).getTime();
    const timeB = new Date(dateB).getTime();
    if (!isNaN(timeA) && !isNaN(timeB)) {
      return fileSortOrder === "desc" ? timeB - timeA : timeA - timeB;
    }
    return fileSortOrder === "desc" ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
  });

  const getFileName = (path) => {
    if (!path) return "—";
    return path.split(/[/\\]/).pop() || path;
  };

  const getBroadcastDate = (dt) => {
    if (!dt) return "";
    return dt.substring(0, 16).replace("T", " ");
  };

  const formatTokens = (n) => {
    if (!n) return "0";
    if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
    if (n >= 1000) return (n / 1000).toFixed(1) + "K";
    return n.toString();
  };

  const formatNumber = (num, minDec, maxDec) => {
    if (num == null || isNaN(num)) return "0";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: minDec,
      maximumFractionDigits: maxDec
    });
  };

  const isLoading = activeTab === "db" ? dbLoading : fileLoading;
  const hasError = activeTab === "db" ? dbError : fileError;

  const isFilterActive =
    fromDate !== "" ||
    toDate !== "" ||
    docTypeFilter !== "ALL";

  return (
    <Box className="page-content-width page-bg admin-page" style={{ padding: "30px" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "450px", gap: 2 }}>
          <CircularProgress size={45} thickness={4} color="primary" />
        </Box>
      ) : hasError ? (
        <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
          <Alert severity="error" sx={{ width: "100%", maxWidth: "600px", borderRadius: "12px", border: "1px solid rgba(211, 47, 47, 0.2)" }}>
            Failed to load data. Please check your network or API connectivity.
          </Alert>
        </Box>
      ) : (
        <Box>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3.5, mt: 1 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-0.03em", color: "text.primary" }}>
                AI Docs Processing Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                OCR &amp; Embedding pipeline stats - announcements and flat-file pipelines
              </Typography>
            </Box>
          </Box>

          {/* Date & Doc Type Filter Container */}
          <Box
            className="admin-card mb-3"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2.5,
              flexWrap: "wrap",
              p: 2,
              borderRadius: "16px"
            }}
          >
            {/* Left side: Date Filter group */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              {/* From Date */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  component="label"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.05em",
                    whiteSpace: "nowrap"
                  }}
                >
                  From:
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: "140px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px"
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              {/* To Date */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  component="label"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.05em",
                    whiteSpace: "nowrap"
                  }}
                >
                  To:
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: "140px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px"
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleApplyFilters}
                sx={{
                  borderRadius: "8px",
                  fontWeight: 700,
                  px: 2.5,
                  py: 0.8,
                  textTransform: "none"
                }}
              >
                Apply
              </Button>
              {isFilterActive && (
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={clearFilters}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 600,
                    px: 2.5,
                    py: 0.8,
                    textTransform: "none",
                    borderColor: "divider"
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>

            {/* Right side: Doc Type Selector group */}
            {activeTab === "db" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: "auto" }}>
                <Typography
                  component="label"
                  sx={{
                    mb: 0,
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.05em"
                  }}
                >
                  Doc Type:
                </Typography>
                <TextField
                  select
                  size="small"
                  value={docTypeFilter}
                  onChange={handleDocTypeChange}
                  sx={{
                    width: "220px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px"
                    }
                  }}
                  SelectProps={{
                    native: true
                  }}
                >
                  {availableDocTypes.map((type) => (
                    <option key={type} value={type}>
                      {type === "ALL" ? "All Doc Types" : type.replace(/_/g, " ")}
                    </option>
                  ))}
                </TextField>
              </Box>
            )}
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            sx={{
              mb: 3,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
                minWidth: 120,
                cursor: "pointer"
              }
            }}
          >
            <Tab label="Announcements (DB)" value="db" />
            {/* <Tab label="Flat-file Pipeline (JSON)" value="file" /> */}
          </Tabs>

          {/* ── DB Panel ── */}
          {activeTab === "db" && (
            <Box>
              {/* Pipeline Stats */}
              <Typography className="section-label" component="p" sx={{ mb: 1 }}>
                Pipeline Stats
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
                <Box className="stat-card" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{dbSummaryItem.totalDocs ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Total Docs</Typography>
                </Box>
                <Box className="stat-card stat-success" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{dbSummaryItem.successCount ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Success</Typography>
                </Box>
                <Box className="stat-card stat-failed" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{dbSummaryItem.failedCount ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Failed</Typography>
                </Box>
                <Box className="stat-card stat-skipped" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{dbSummaryItem.skippedCount ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Skipped</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{dbSummaryItem.avgOcrMs ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Avg OCR (ms)</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{dbSummaryItem.avgEmbeddingMs ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Avg Embed (ms)</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{dbSummaryItem.avgTotalMs ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Avg Total (ms)</Typography>
                </Box>
              </Box>

              {/* Cost Summary */}
              <Typography className="section-label" component="p" sx={{ mb: 1 }}>
                OCR Cost Summary <span className="text-muted fw-normal" style={{ fontSize: ".8em" }}>(for records shown below)</span>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
                <Box className="stat-card stat-cost" sx={{ minWidth: "180px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">₹{formatNumber(dbTotalCostInr, 2, 2)}</Typography>
                  <Typography className="stat-lbl" component="div">Total Cost (INR)</Typography>
                </Box>
                <Box className="stat-card stat-cost" sx={{ minWidth: "180px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">${formatNumber(dbTotalCostUsd, 3, 4)}</Typography>
                  <Typography className="stat-lbl" component="div">Total Cost (USD)</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "180px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">₹{formatNumber(dbAvgCostInr, 2, 2)}</Typography>
                  <Typography className="stat-lbl" component="div">Avg Cost / Doc (INR)</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "180px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{formatTokens(dbTotalInputTokens)}</Typography>
                  <Typography className="stat-lbl" component="div">Total Input Tokens</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "180px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{formatTokens(dbTotalOutputTokens)}</Typography>
                  <Typography className="stat-lbl" component="div">Total Output Tokens</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "180px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{dbUsdToInrRate || "—"}</Typography>
                  <Typography className="stat-lbl" component="div">USD → INR Rate</Typography>
                </Box>
              </Box>

              {/* Table Container */}
              <Box className="admin-card p-0" sx={{ border: "1px solid rgba(224, 224, 224, 1)", mb: 3, borderRadius: '10px' }}>
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "16px 16px 0 0" }}>
                  <Table className="table table-hover mb-0 pl-3">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Symbol</TableCell>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Doc Type</TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>OCR (ms)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Embed (ms)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Total (ms)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>In Tokens</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Out Tokens</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Cost (USD)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Cost (INR)</TableCell>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                          <TableSortLabel
                            active={true}
                            direction={dbSortOrder}
                            onClick={() => setDbSortOrder(dbSortOrder === "desc" ? "asc" : "desc")}
                          >
                            Broadcast Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedDbRows.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell className="fw-semibold" sx={{ whiteSpace: "nowrap" }}>{row.aiDocsProcessKey?.symbol || ""}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {(row.aiDocsProcessKey?.documentType || "").replace(/_/g, " ")}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                            <span className={`status-badge status-${(row.vectorStatus || "unknown").toLowerCase()}`}>
                              {row.vectorStatus || "—"}
                            </span>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{row.ocrTimeMs || 0}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{row.embeddingTimeMs || 0}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{row.vectorTotalTimeMs || 0}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{formatTokens(row.ocrInputTokens)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{formatTokens(row.ocrOutputTokens)}</TableCell>
                          <TableCell align="right" className="cost-usd" sx={{ whiteSpace: "nowrap" }}>${formatNumber(row.ocrCostUsd, 4, 6)}</TableCell>
                          <TableCell align="right" className="cost-inr" sx={{ whiteSpace: "nowrap" }}>₹{formatNumber(row.ocrCostInr, 2, 4)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{getBroadcastDate(row.aiDocsProcessKey?.broadcastingDateTime)}</TableCell>
                          <TableCell className={row.vectorError ? "text-danger small center" : "text-muted"} sx={{ minWidth: 220 }}>
                            {row.vectorError || "—"}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Totals footer row */}
                      {sortedDbRows.length > 0 && (
                        <TableRow className="totals-row" style={{ marginLeft: "20px" }}>
                          <TableCell colSpan={6} sx={{ fontWeight: "bold", textAlign: "right" }}>
                            Totals ({sortedDbRows.length} records)
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>{formatTokens(dbTotalInputTokens)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>{formatTokens(dbTotalOutputTokens)}</TableCell>
                          <TableCell align="right" className="cost-usd" sx={{ fontWeight: "bold" }}>
                            ${formatNumber(dbTotalCostUsd, 4, 4)}
                          </TableCell>
                          <TableCell align="right" className="cost-inr" sx={{ fontWeight: "bold" }}>
                            ₹{formatNumber(dbTotalCostInr, 2, 2)}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                      )}

                      {sortedDbRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={12} sx={{ textAlign: "center", color: "text.secondary", py: 3 }}>
                            No records found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Table Pagination */}
                <TablePagination
                  component="div"
                  count={dbTotalCount}
                  page={dbPage}
                  onPageChange={handleDbPageChange}
                  rowsPerPage={dbRowsPerPage}
                  onRowsPerPageChange={handleDbRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    borderRadius: "0 0 16px 16px"
                  }}
                />
              </Box>
            </Box>
          )}

          {/* ── File Panel ── */}
          {activeTab === "file" && (
            <Box>
              {/* Pipeline Stats */}
              <Typography className="section-label" component="p" sx={{ mb: 1 }}>
                Pipeline Stats
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
                <Box className="stat-card" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{fileSummaryItem.totalFiles ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Total Files</Typography>
                </Box>
                <Box className="stat-card stat-success" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{fileSummaryItem.successCount ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Success</Typography>
                </Box>
                <Box className="stat-card stat-failed" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{fileSummaryItem.failedCount ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Failed</Typography>
                </Box>
                <Box className="stat-card stat-skipped" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{fileSummaryItem.skippedCount ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Skipped</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{fileSummaryItem.avgOcrMs ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Avg OCR (ms)</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{fileSummaryItem.avgEmbeddingMs ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Avg Embed (ms)</Typography>
                </Box>
                <Box className="stat-card" sx={{ minWidth: "140px", flex: "1 0 auto" }}>
                  <Typography className="stat-val" component="div">{fileSummaryItem.avgTotalMs ?? "—"}</Typography>
                  <Typography className="stat-lbl" component="div">Avg Total (ms)</Typography>
                </Box>
              </Box>

              {/* Table Container */}
              <Box className="admin-card p-0" sx={{ border: "1px solid rgba(224, 224, 224, 1)", mb: 3 }}>
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "16px 16px 0 0" }}>
                  <Table className="table table-hover mb-0">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>File</TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>OCR (ms)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Embed (ms)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Total (ms)</TableCell>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                          <TableSortLabel
                            active={true}
                            direction={fileSortOrder}
                            onClick={() => setFileSortOrder(fileSortOrder === "desc" ? "asc" : "desc")}
                          >
                            Timestamp
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedFileRows.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell title={row.fileName || ""} sx={{ whiteSpace: "nowrap" }}>{getFileName(row.fileName)}</TableCell>
                          <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                            <span className={`status-badge status-${(row.status || "unknown").toLowerCase()}`}>
                              {row.status || "—"}
                            </span>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{row.ocrTimeMs || 0}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{row.embeddingTimeMs || 0}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{row.timeTakenMs || 0}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{row.timestamp ? row.timestamp.substring(0, 19).replace("T", " ") : ""}</TableCell>
                          <TableCell className="small text-muted" sx={{ minWidth: 220 }}>{row.reason || "—"}</TableCell>
                        </TableRow>
                      ))}

                      {fileRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: "center", color: "text.secondary", py: 3 }}>
                            No records found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Table Pagination */}
                <TablePagination
                  component="div"
                  count={fileTotalCount}
                  page={filePage}
                  onPageChange={handleFilePageChange}
                  rowsPerPage={fileRowsPerPage}
                  onRowsPerPageChange={handleFileRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    borderRadius: "0 0 16px 16px"
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}